import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Tab,
} from '@mui/material';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';
import { SolaForceTabs } from '~/components/SolaForceTabs';
import { ProposalType } from '~/data/proposals';
import { usePrevious } from '~/hooks/usePrevious';
import { WorkflowsEmployeeProposalsDialog_OnUpdateLegacy } from '~/library/WorkflowsEmployeeProposalsDialog/component';
import { EmployeeProposalsTabs } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog';
import { EmployeeProposals } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposals';
import { EmployeeProposalsHistory } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposalsHistory';
import {
  EmployeeProposalsTab,
  EmployeeRewardingProposalDialogTabs,
  getEmployeeProposalsTabsTranslationKey,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/data';

import { useGetProposalsTabs } from './useGetProposalsTabs';

export type EmployeeProposalsDialogProps =
  WorkflowsEmployeeProposalsDialog_OnUpdateLegacy & {
    open: boolean;
    openOnTab?: EmployeeProposalsTabs;
    onClose: () => void;
    enabledDialogTabs: EmployeeRewardingProposalDialogTabs[];
  };

export const EmployeeProposalsDialog = (
  props: EmployeeProposalsDialogProps,
) => {
  const { open, openOnTab, onClose, enabledDialogTabs, onUpdateLegacy } = props;
  const { t } = useTranslation();
  const {
    isLoading,
    proposalsTabs,
    defaultProposalsTab,
    proposalHistoryTabs,
    defaultProposalHistoryTab,
    newWorkflowsEngineEnabled,
  } = useGetProposalsTabs({
    enabledDialogTabs,
  });
  const prevOpen = usePrevious(open);

  const [selectedTab, setSelectedTab] = useState<EmployeeProposalsTabs | false>(
    !!openOnTab ? openOnTab : defaultProposalsTab?.name ?? false,
  );

  const [selectedHistoryTab, setSelectedHistoryTab] = useState<
    EmployeeProposalsTabs | false
  >(defaultProposalHistoryTab?.name ?? false);

  useEffect(() => {
    const isBeingOpened = open && prevOpen !== open;
    const shouldReset = !!openOnTab && openOnTab !== selectedTab;
    if (isBeingOpened && shouldReset) {
      setSelectedTab(openOnTab);
    }
  }, [open, prevOpen, openOnTab, selectedTab]);

  const handleClose = () => {
    onClose();
  };

  const getProposalTypeByTab = useCallback(
    (tab: EmployeeProposalsTabs): ProposalType => {
      const tabType: { [key in EmployeeProposalsTabs]?: ProposalType } = {
        [EmployeeProposalsTabs.SalaryProposal]: ProposalType.SALARY_PROPOSAL,
        [EmployeeProposalsTabs.ShortTermIncentivesProposal]:
          ProposalType.BONUS_PROPOSAL,
        [EmployeeProposalsTabs.BillableHoursProposal]:
          ProposalType.BILLABLE_HOURS,
        [EmployeeProposalsTabs.BenefitProposal]: ProposalType.BENEFIT_PROPOSAL,
        [EmployeeProposalsTabs.AllowanceProposal]:
          ProposalType.ALLOWANCE_PROPOSAL,
      };

      return tabType[tab] || ProposalType.SALARY_PROPOSAL;
    },
    [],
  );

  const renderHistoryTab = useCallback(() => {
    const historyTabs = proposalHistoryTabs
      .filter((t) => t.available)
      .map((t) => t.name);

    return (
      <>
        <SolaForceTabs
          value={selectedHistoryTab}
          onChange={(e, v) => {
            setSelectedHistoryTab(v);
          }}
          background={'fade'}
        >
          {historyTabs.map((category) => (
            <Tab
              key={category}
              label={t(getEmployeeProposalsTabsTranslationKey(category))}
              data-cy={category}
              value={category}
            />
          ))}
        </SolaForceTabs>
        <DialogContent>
          <EmployeeProposalsHistory activeTab={selectedHistoryTab} />
        </DialogContent>
      </>
    );
  }, [t, selectedHistoryTab, setSelectedHistoryTab, proposalHistoryTabs]);

  const renderTab = useCallback(
    (tab: EmployeeProposalsTabs) => {
      const tabConfig = proposalsTabs.find((t) => t.name === tab);

      if (!tabConfig || !tabConfig?.available) {
        return (
          <DialogContent>
            <Alert severity={'info'}>{t('laNoContent')}</Alert>
          </DialogContent>
        );
      }

      switch (tab) {
        case EmployeeProposalsTabs.SalaryProposal:
        case EmployeeProposalsTabs.ShortTermIncentivesProposal:
        case EmployeeProposalsTabs.BillableHoursProposal:
        case EmployeeProposalsTabs.BenefitProposal:
        case EmployeeProposalsTabs.AllowanceProposal:
          const noWorkflowConfigured =
            typeof tabConfig.hasWorkflowConfigured !== 'undefined' &&
            !tabConfig.hasWorkflowConfigured;

          if (noWorkflowConfigured || !newWorkflowsEngineEnabled) {
            return (
              <DialogContent>
                <Alert severity={'info'}>{`${t(
                  'createNewProposalUnavailable',
                )}: ${t(
                  newWorkflowsEngineEnabled
                    ? 'noWorkflowConfigured'
                    : 'workflowsEngineDisabled',
                )}`}</Alert>
              </DialogContent>
            );
          }

          return (
            <DialogContent>
              <EmployeeProposals
                proposalType={getProposalTypeByTab(tab)}
                onUpdateLegacy={onUpdateLegacy}
              />
            </DialogContent>
          );
        case EmployeeProposalsTabs.ProposalsHistory:
          return renderHistoryTab();
        default:
          return <DialogContent></DialogContent>;
      }
    },
    [
      proposalsTabs,
      getProposalTypeByTab,
      renderHistoryTab,
      onUpdateLegacy,
      t,
      newWorkflowsEngineEnabled,
    ],
  );

  const renderProposalsTab = useMemo(() => {
    return proposalsTabs
      .filter((t: EmployeeProposalsTab) => t.available)
      .map((tab) => (
        <Tab
          key={tab.name}
          label={t(getEmployeeProposalsTabsTranslationKey(tab.name))}
          data-cy={tab.name}
          value={tab.name}
          disabled={tab.disabled}
        />
      ));
  }, [proposalsTabs, t]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth={true}
      maxWidth={'xl'}
      PaperProps={{
        style: {
          minHeight: '90%',
          maxHeight: '90%',
        },
      }}
    >
      <SolaForceDialogTitle onClose={handleClose}>
        {t('laProposals')}
      </SolaForceDialogTitle>
      {!isLoading && (
        <>
          <SolaForceTabs
            value={selectedTab}
            onChange={(e, v) => {
              setSelectedTab(v);
            }}
          >
            {renderProposalsTab}
          </SolaForceTabs>
          {!!selectedTab && renderTab(selectedTab)}
        </>
      )}
      <Divider />
      <DialogActions>
        <Button onClick={handleClose}>{t('close')}</Button>
      </DialogActions>
    </Dialog>
  );
};
